/**
 * Process intake — the agent interviews the expert
 *
 * Before the finance agent advises anyone, it asks the person doing the work
 * how the work is done. This is that conversation, as a form.
 *
 * Two things make this screen worth building rather than skipping:
 *
 * 1. Every question shows WHY it is being asked. An intake that reads like an
 *    audit gets the answers an audit gets.
 * 2. Numeric answers are compared against the ledger on submit. When they
 *    disagree, the gap is shown immediately — and that gap is usually the most
 *    valuable thing on the page, because it means either the exception has
 *    become the rule or something is not being captured where the expert
 *    thinks it is.
 *
 * A disagreement blocks its own playbook and nothing else. One open question
 * about invoice approval does not silence the agent on collections.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, PageHead, Note, Tag, Split } from '../../components/ui';
import {
  Contradiction,
  PlaybookBlock,
  QuestionSetView,
  ResolutionOutcome,
  SubmitResult,
  processService,
} from '../services/process.service';

type Draft = Record<string, string>;

const OUTCOME_LABEL: Record<ResolutionOutcome, string> = {
  data_accepted: 'The data is right — my number was wrong',
  explained: 'Both are right — here is why they differ',
  dismissed: 'The measurement is wrong',
};

export const ProcessIntakePage: React.FC = () => {
  const [sets, setSets] = useState<QuestionSetView[]>([]);
  const [blocks, setBlocks] = useState<PlaybookBlock[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [owner, setOwner] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const [contradictions, setContradictions] = useState<Contradiction[]>([]);
  const [settling, setSettling] = useState<Contradiction | null>(null);
  const [outcome, setOutcome] = useState<ResolutionOutcome>('explained');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [questionData, contradictionData] = await Promise.all([
        processService.listQuestionSets(),
        processService.listContradictions(),
      ]);
      setSets(questionData.sets);
      setBlocks(questionData.blocks);
      setContradictions(contradictionData.open);

      // Pre-fill from what was answered before, so revisiting an intake is a
      // correction rather than a retype.
      setDrafts((current) => {
        const next = { ...current };
        for (const set of questionData.sets) {
          if (next[set.key]) continue;
          const draft: Draft = {};
          for (const answer of set.answers) {
            draft[answer.questionKey] = Array.isArray(answer.value)
              ? answer.value.join(', ')
              : String(answer.value);
          }
          next[set.key] = draft;
        }
        return next;
      });

      const named = questionData.sets.find((s) => s.ownerLabel);
      if (named?.ownerLabel) setOwner((o) => o || named.ownerLabel!);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the questions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setField = useCallback((setKey: string, questionKey: string, value: string) => {
    setDrafts((current) => ({
      ...current,
      [setKey]: { ...(current[setKey] ?? {}), [questionKey]: value },
    }));
  }, []);

  const submit = useCallback(
    async (set: QuestionSetView) => {
      if (owner.trim().length < 2) {
        setError(
          'Your name or role is needed. An unattributed account of a process is a ' +
            'rumour, and the agent will not act on one.'
        );
        return;
      }

      const draft = drafts[set.key] ?? {};
      const answers = set.questions
        .map((question) => {
          const raw = (draft[question.key] ?? '').trim();
          if (!raw) return null;
          return {
            questionKey: question.key,
            value: question.kind === 'number' ? Number(raw) : raw,
          };
        })
        .filter((a): a is { questionKey: string; value: string | number } => {
          if (!a) return false;
          // A number field that was not a number is worse than blank: it would
          // be compared against the ledger and produce a nonsense gap.
          return !(typeof a.value === 'number' && Number.isNaN(a.value));
        });

      setBusy(true);
      setError(null);
      setResult(null);
      try {
        setResult(await processService.submit(set.key, answers, owner.trim()));
        setLoading(true);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'The intake was not saved.');
      } finally {
        setBusy(false);
      }
    },
    [drafts, owner, load]
  );

  const settle = useCallback(async () => {
    if (!settling) return;
    if (note.trim().length < 10) {
      setError(
        'An explanation is needed. It is the part the agent keeps — closing this ' +
          'without it just hides the disagreement.'
      );
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await processService.resolve(settling.id, outcome, note.trim());
      setSettling(null);
      setNote('');
      setLoading(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }, [settling, outcome, note, load]);

  const answeredCount = useMemo(
    () => sets.filter((s) => s.completedAt).length,
    [sets]
  );

  return (
    <AppShell
      title="Process intake"
      sub={`${answeredCount}/${sets.length} complete · ${contradictions.length} open disagreement${
        contradictions.length === 1 ? '' : 's'
      }`}
    >
      <PageHead
        eyebrow="Before advice"
        title="How does the work actually run?"
        lede="The agent can read your ledger. It cannot read why a second signature exists, which customer must never be chased, or what people do when the normal route is too slow. Those are the things that turn correct arithmetic into reckless advice, so it asks first."
      />

      {error && (
        <Note style={{ marginBottom: 12 }}>
          <strong>{error}</strong>
        </Note>
      )}

      <Card title="Who is answering" className="mt">
        <p style={{ marginBottom: 8 }}>
          Recorded against every answer. The agent cites you by name when an answer
          changes what it recommends.
        </p>
        <input
          className="inp"
          placeholder="e.g. Mona Hassan, Finance Manager"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
        />
      </Card>

      {contradictions.length > 0 && (
        <Card title="Where your account and the ledger disagree" className="mt">
          <p style={{ marginBottom: 10 }}>
            Neither side is assumed wrong. A gap this size usually means the exception
            has become the rule, or something is not being captured where you think it
            is. Until it is settled, the agent will not advise on the affected
            process — advice built on a disputed picture of the work is worse than no
            advice.
          </p>

          {contradictions.map((c) => (
            <div key={c.id} className="mt" style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
              <div style={{ marginBottom: 6 }}>
                <Tag tone="r">{c.differencePercent}% apart</Tag>{' '}
                {c.playbookKey ? (
                  <Tag tone="a">blocks {c.playbookKey.replace(/_/g, ' ')}</Tag>
                ) : (
                  <Tag tone="d">blocks nothing</Tag>
                )}
              </div>
              <Split label={`${c.statedBy} said`} value={String(c.statedValue)} />
              <Split label={`${c.metricKey} measures`} value={String(c.measuredValue)} />
              <button
                className="btn btn-p mt"
                onClick={() => {
                  setSettling(c);
                  setOutcome('explained');
                  setNote('');
                }}
              >
                Settle this
              </button>
            </div>
          ))}
        </Card>
      )}

      {settling && (
        <Card title="Settling a disagreement" className="mt">
          <p style={{ marginBottom: 10 }}>
            <strong>{settling.statedBy}</strong> said {settling.statedValue};{' '}
            <strong>{settling.metricKey}</strong> measures {settling.measuredValue}.
          </p>

          <div className="sec-t">What is going on</div>
          {(Object.keys(OUTCOME_LABEL) as ResolutionOutcome[]).map((key) => (
            <label key={key} style={{ display: 'block', marginBottom: 6 }}>
              <input
                type="radio"
                name="outcome"
                checked={outcome === key}
                onChange={() => setOutcome(key)}
              />{' '}
              {OUTCOME_LABEL[key]}
            </label>
          ))}

          <div className="sec-t">Explain it</div>
          <p style={{ marginBottom: 6 }}>
            This is the part the agent keeps and cites. &ldquo;Two days is how long it
            takes once it reaches me; it sits in the shared inbox before that&rdquo; is
            worth more to it than the number ever was.
          </p>
          <textarea
            className="inp"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What explains the gap?"
          />
          <div className="mt">
            <button className="btn btn-p" disabled={busy} onClick={() => void settle()}>
              Record it
            </button>{' '}
            <button className="btn btn-g" disabled={busy} onClick={() => setSettling(null)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      {loading && (
        <Card className="mt">
          <p>Loading the questions…</p>
        </Card>
      )}

      {!loading &&
        sets.map((set) => {
          const draft = drafts[set.key] ?? {};
          const block = blocks.find((b) => b.playbookKey === set.playbookKey);
          const isOpen = open === set.key;

          return (
            <Card
              key={set.key}
              title={set.label}
              className="mt"
            >
              <div style={{ marginBottom: 8 }}>
                {set.completedAt ? (
                  <Tag tone="g">complete</Tag>
                ) : (
                  <Tag tone="a">not answered</Tag>
                )}{' '}
                {set.playbookKey ? (
                  <Tag tone="b">{set.playbookKey.replace(/_/g, ' ')}</Tag>
                ) : (
                  <Tag tone="d">department-wide</Tag>
                )}{' '}
                {set.blocked && <Tag tone="r">playbook blocked</Tag>}
                {set.ownerLabel && (
                  <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>
                    answered by {set.ownerLabel}
                    {set.reviewAfter ? ` · confirm again after ${set.reviewAfter}` : ''}
                  </span>
                )}
              </div>

              <p style={{ marginBottom: 10 }}>{set.intro}</p>

              {block && (
                <Note style={{ marginBottom: 10 }}>
                  <strong>This playbook is paused.</strong> {block.message} Waiting on{' '}
                  {block.waitingOn}.
                </Note>
              )}

              {!isOpen && (
                <button className="btn btn-p" onClick={() => setOpen(set.key)}>
                  {set.completedAt ? 'Review my answers' : 'Answer these'}
                </button>
              )}

              {isOpen && (
                <>
                  {set.questions.map((question) => (
                    <div key={question.key} className="mt">
                      <div className="sec-t">
                        {question.prompt}
                        {question.required ? '' : ' (optional)'}
                      </div>
                      <p style={{ marginBottom: 6, fontSize: 13, opacity: 0.8 }}>
                        {question.why}
                      </p>

                      {question.kind === 'choice' && question.choices ? (
                        <select
                          className="inp"
                          value={draft[question.key] ?? ''}
                          onChange={(e) => setField(set.key, question.key, e.target.value)}
                        >
                          <option value="">—</option>
                          {question.choices.map((choice) => (
                            <option key={choice} value={choice}>
                              {choice}
                            </option>
                          ))}
                        </select>
                      ) : question.kind === 'number' ? (
                        <input
                          className="inp"
                          type="number"
                          step="any"
                          value={draft[question.key] ?? ''}
                          onChange={(e) => setField(set.key, question.key, e.target.value)}
                        />
                      ) : (
                        <textarea
                          className="inp"
                          rows={3}
                          value={draft[question.key] ?? ''}
                          onChange={(e) => setField(set.key, question.key, e.target.value)}
                        />
                      )}

                      {question.checkAgainst && (
                        <p style={{ marginTop: 4, fontSize: 12, opacity: 0.7 }}>
                          Checked against <code>{question.checkAgainst.metricKey}</code>. A
                          gap over {question.checkAgainst.tolerancePercent}% opens a
                          disagreement for you to settle.
                        </p>
                      )}
                    </div>
                  ))}

                  <div className="mt">
                    <button
                      className="btn btn-p"
                      disabled={busy}
                      onClick={() => void submit(set)}
                    >
                      Save this
                    </button>{' '}
                    <button className="btn btn-g" disabled={busy} onClick={() => setOpen(null)}>
                      Close
                    </button>
                  </div>
                </>
              )}
            </Card>
          );
        })}

      {result && (
        <Card title="Saved" className="mt">
          {result.contradictions.length === 0 && result.missingRequired.length === 0 && (
            <p>
              Recorded, and nothing in it contradicts the ledger. The agent will cite
              these answers when they change what it recommends.
            </p>
          )}

          {result.contradictions.length > 0 && (
            <p>
              Recorded — and {result.contradictions.length} of your answers disagrees
              with what the ledger measures. That is listed above. It is worth more
              than the answer that prompted it.
            </p>
          )}

          {result.missingRequired.length > 0 && (
            <p style={{ marginTop: 8 }}>
              Still blank: {result.missingRequired.join(', ')}. A partial account is
              better than none, so this was saved — but the playbook stays paused until
              they are answered.
            </p>
          )}
        </Card>
      )}
    </AppShell>
  );
};

export default ProcessIntakePage;
